import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * The contract between `AppShell` and the screens inside it.
 *
 * Two things in the utility row belong to the page rather than to the shell —
 * the search field and the help button — and both are *claimed* by the
 * screen rather than always drawn. Claiming is the point: the design puts a
 * search box on every list page, but the gateway has no search endpoint and
 * a client-side filter only sees the page that is loaded (see the map's risk
 * 8, "search boxes that silently lie"). A screen that cannot search must not
 * inherit a box that finds nothing, so the shell renders neither control
 * until a screen asks for one and says what it searches.
 *
 * Everything here degrades to a no-op outside `AppShell`, so a screen can be
 * rendered on its own in a test without a provider.
 */

export interface ShellSearchSpec {
  /**
   * What this box searches, in the user's words. It doubles as the
   * accessible name, so it must be scoped and honest — "Formulare
   * durchsuchen", "In dieser Seite suchen" — never a bare "Suchen".
   */
  placeholder: string
  /** Called on submit (the button or Enter). Typing alone updates `query`. */
  onSearch?: (value: string) => void
}

export interface ShellHelpSpec {
  /** Accessible name of the icon button. */
  label?: string
  onOpen: () => void
}

interface ShellChromeValue {
  search: ShellSearchSpec | null
  registerSearch: (spec: ShellSearchSpec | null) => void
  query: string
  setQuery: (value: string) => void
  help: ShellHelpSpec | null
  registerHelp: (spec: ShellHelpSpec | null) => void
}

const noop = () => {}
const fallback: ShellChromeValue = {
  search: null,
  registerSearch: noop,
  query: '',
  setQuery: noop,
  help: null,
  registerHelp: noop,
}

const ShellChromeContext = createContext<ShellChromeValue>(fallback)

/** Used by `AppShell`; screens use the two hooks below. */
export function useShellChrome(): ShellChromeValue {
  return useContext(ShellChromeContext)
}

export function ShellChromeProvider({
  value,
  children,
}: {
  value: ShellChromeValue
  children: ReactNode
}) {
  return <ShellChromeContext.Provider value={value}>{children}</ShellChromeContext.Provider>
}

/**
 * Claims the shell's search field for as long as this screen is mounted, and
 * hands back what is typed in it.
 *
 * `onSearch` is kept in a ref so a screen can pass an inline arrow function
 * without re-registering on every render — the placeholder is the only thing
 * that has to be watched.
 */
export function useShellSearch(spec?: ShellSearchSpec): {
  query: string
  setQuery: (value: string) => void
} {
  const { registerSearch, query, setQuery } = useShellChrome()
  const onSearch = useRef(spec?.onSearch)
  onSearch.current = spec?.onSearch

  const placeholder = spec?.placeholder
  useEffect(() => {
    if (!placeholder) return
    registerSearch({ placeholder, onSearch: (value) => onSearch.current?.(value) })
    return () => registerSearch(null)
  }, [placeholder, registerSearch])

  return { query, setQuery }
}

/** Claims the utility row's help button while this screen is mounted. */
export function useShellHelp(spec: ShellHelpSpec | null) {
  const { registerHelp } = useShellChrome()
  const onOpen = useRef(spec?.onOpen)
  onOpen.current = spec?.onOpen

  const label = spec?.label
  const enabled = spec != null
  useEffect(() => {
    if (!enabled) return
    registerHelp({ label, onOpen: () => onOpen.current?.() })
    return () => registerHelp(null)
  }, [enabled, label, registerHelp])
}

/**
 * The one `<h1>` a screen has, in the design's `pageTitle` ramp — 32px at
 * phone width, as `PhoneUebersicht` draws it, 40px from `md` up.
 *
 * It is a component rather than a class in every screen so that the ramp
 * step and the single-h1 rule live in one place; a screen that needs a
 * different element (a breadcrumb header, say) simply does not use it.
 */
export function PageTitle({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h1 className={cn('text-dialog-title text-text md:text-page-title', className)} {...props}>
      {children}
    </h1>
  )
}

/**
 * Builds the value `AppShell` provides. Split out so the shell's own render
 * stays about layout, and so the reset-on-navigation rule has somewhere to
 * be stated: a search term typed on Übersicht must not still be filtering
 * Archiv a click later.
 */
export function useShellChromeState(routeKey: string): ShellChromeValue {
  const [search, setSearch] = useState<ShellSearchSpec | null>(null)
  const [help, setHelp] = useState<ShellHelpSpec | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    setQuery('')
  }, [routeKey])

  const registerSearch = useCallback((spec: ShellSearchSpec | null) => {
    setSearch(spec)
    if (!spec) setQuery('')
  }, [])
  const registerHelp = useCallback((spec: ShellHelpSpec | null) => setHelp(spec), [])

  return useMemo(
    () => ({ search, registerSearch, query, setQuery, help, registerHelp }),
    [search, registerSearch, query, help, registerHelp],
  )
}
